import React, { useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl } from "spark-core-dx/components";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA,
  ENTITY_SCHEMA_MODE_MANUAL,
  getEntitySchemaMode,
  getEntitySchemaModeLabel,
  getEntitySchemaSetupModes,
  loadDestinationSchemaOptions,
  normalizeEntitySchemaMode
} from "./entitySchemaStepUtils";
import { readValue } from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const EntitySchemaModeStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const externalSystemMgr = resourceDomainContext?.managers?.externalSystem;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const resourceConfigurationMgr = resourceDomainContext?.managers?.resourceConfiguration;
  const modes = useMemo(() => getEntitySchemaSetupModes(wizard), [wizard]);
  const hasDestinationSchemaMode = useMemo(
    () => modes.some((setupMode) =>
      normalizeEntitySchemaMode(readValue(setupMode, "key", "Key", "value", "Value")) === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA
    ),
    [modes]
  );
  const destinationSchemaManagerReady = Boolean(externalSystemMgr?.Query && entityObjectMgr?.Query && resourceConfigurationMgr?.Query);
  const [mode, setMode] = useState(() => getEntitySchemaMode(wizard, values) || ENTITY_SCHEMA_MODE_MANUAL);
  const [destinationSchemaAvailability, setDestinationSchemaAvailability] = useState({ loading: false, available: null });
  const modeRef = useRef(mode);
  const externalSystemMgrRef = useRef(externalSystemMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const resourceConfigurationMgrRef = useRef(resourceConfigurationMgr);
  const coreStateRef = useRef(coreState);
  const destinationSchemaLoadKeyRef = useRef("");
  modeRef.current = mode;
  externalSystemMgrRef.current = externalSystemMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  resourceConfigurationMgrRef.current = resourceConfigurationMgr;
  coreStateRef.current = coreState;

  useEffect(() => {
    const nextMode = getEntitySchemaMode(wizard, values) || ENTITY_SCHEMA_MODE_MANUAL;
    setMode(nextMode);
  }, [values, wizard]);

  useEffect(() => {
    if (!hasDestinationSchemaMode) {
      destinationSchemaLoadKeyRef.current = "";
      setDestinationSchemaAvailability((previous) =>
        previous.loading || previous.available !== null ? { loading: false, available: null } : previous
      );
      return undefined;
    }

    if (!destinationSchemaManagerReady) {
      destinationSchemaLoadKeyRef.current = "";
      setDestinationSchemaAvailability((previous) =>
        previous.loading || previous.available !== false ? { loading: false, available: false } : previous
      );
      return undefined;
    }

    const loadKey = "entity-schema-destination-mode-availability";
    if (destinationSchemaLoadKeyRef.current === loadKey) return undefined;
    destinationSchemaLoadKeyRef.current = loadKey;

    let mounted = true;
    const load = async () => {
      setDestinationSchemaAvailability((previous) => ({ loading: true, available: previous.available }));
      try {
        const options = await loadDestinationSchemaOptions({
          externalSystemMgr: externalSystemMgrRef.current,
          entityObjectMgr: entityObjectMgrRef.current,
          resourceConfigurationMgr: resourceConfigurationMgrRef.current,
          coreState: coreStateRef.current
        });
        if (mounted) setDestinationSchemaAvailability({ loading: false, available: options.length > 0 });
      } catch {
        if (mounted) setDestinationSchemaAvailability({ loading: false, available: false });
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [destinationSchemaManagerReady, hasDestinationSchemaMode]);

  useEffect(() => {
    if (
      !readOnly
      && !saving
      && destinationSchemaAvailability.available === false
      && modeRef.current === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA
    ) {
      setMode(ENTITY_SCHEMA_MODE_MANUAL);
      onChange?.("SchemaSetupMode", ENTITY_SCHEMA_MODE_MANUAL);
    }
  }, [destinationSchemaAvailability.available, onChange, readOnly, saving]);

  useEffect(() => {
    registerStepController?.({
      prepareSave: async (currentValues = {}) => ({
        ...currentValues,
        SchemaSetupMode: modeRef.current
      }),
      prepareContinue: async (currentValues = {}) => ({
        ...currentValues,
        SchemaSetupMode: modeRef.current
      })
    });
    return () => registerStepController?.(null);
  }, [registerStepController]);

  const selectMode = (nextMode) => {
    const normalized = normalizeEntitySchemaMode(nextMode);
    setMode(normalized);
    onChange?.("SchemaSetupMode", normalized);
  };

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title={`Setup Mode: ${getEntitySchemaModeLabel(wizard, mode)}`}
        description="Choose how this Entity Schema is configured for this resource: build it manually, generate it from selected Source Objects, generate EDI Entity Objects, or start from available External System destination schemas."
      />
      <div className="resource-wizard__choice-grid resource-wizard__choice-grid--three">
        {modes.map((setupMode) => {
          const key = normalizeEntitySchemaMode(readValue(setupMode, "key", "Key", "value", "Value"));
          const active = key === mode;
          const isDestinationSchemaMode = key === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA;
          const destinationSchemaDisabled = isDestinationSchemaMode
            && (destinationSchemaAvailability.loading || destinationSchemaAvailability.available === false);
          const disabledReason = destinationSchemaAvailability.loading
            ? "Checking available External System destination schemas."
            : "No External System destination schemas are available for this client.";
          const description = destinationSchemaDisabled
            ? disabledReason
            : readValue(setupMode, "description", "Description") || "";
          return (
            <button
              key={key}
              type="button"
              className={`resource-wizard__choice-card${active ? " resource-wizard__choice-card--active" : ""}`}
              onClick={() => selectMode(key)}
              disabled={readOnly || saving || destinationSchemaDisabled}
              aria-pressed={active}
            >
              <span>{readValue(setupMode, "label", "Label", "text", "Text") || key}</span>
              <small>{description}</small>
            </button>
          );
        })}
      </div>
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={() => onSave?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={() => onContinue?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save & Continue
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

export default EntitySchemaModeStep;
